import type { SearchProfile } from "@shared/types";
import { createFileRoute } from "@tanstack/react-router";
import { Heart, HeartOff } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { useToggleFavorite } from "@/api/mutations/profile";
import { usePublicConfig } from "@/api/queries/config";
import { useFavorites } from "@/api/queries/friends";
import { HotFlirtsSlider } from "@/components/shared";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Pagination } from "@/components/shared/Pagination";
import { ProfileCard } from "@/components/shared/ProfileCard";
import { ProfileGrid } from "@/components/shared/ProfileGrid";
import { Button } from "@/components/ui/Button";
import { usePageMeta } from "@/hooks/use-page-meta";

const favoritesSearchSchema = z.object({
	page: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_app/favorites/")({
	validateSearch: favoritesSearchSchema,
	component: FavoritesPage,
});

function FavoritesPage() {
	const { page = 1 } = Route.useSearch();
	const navigate = Route.useNavigate();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Favoriten - ${config.seo.defaultTitle}` : undefined,
	});

	const { data, isLoading } = useFavorites(page);
	const toggleFavorite = useToggleFavorite();

	const [removeTarget, setRemoveTarget] = useState<SearchProfile | null>(null);

	const profiles = data?.profiles ?? [];
	const perPage = data?.perPage ?? 20;
	const total = data?.results ?? 0;
	const currentPage = page;
	const lastPage = perPage > 0 ? Math.ceil(total / perPage) : 1;

	const handlePageChange = (newPage: number) => {
		navigate({
			search: { page: newPage },
			replace: true,
		});
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleConfirmRemove = () => {
		if (!removeTarget) return;
		toggleFavorite.mutate(
			{ id: removeTarget.id, name: removeTarget.name, isFavorite: true },
			{ onSuccess: () => setRemoveTarget(null) },
		);
	};

	return (
		<div className="space-y-6">
			<h1 className="text-2xl font-bold text-text">Favoriten</h1>

			<ProfileGrid
				profiles={profiles}
				isLoading={isLoading}
				emptyMessage="Keine Favoriten"
				emptyDescription="Du hast noch keine Profile als Favorit markiert. Besuche Profile und klicke auf das Herz-Symbol."
				emptyIcon={<Heart size={48} />}
				renderCard={(profile) => (
					<div className="flex flex-col gap-2">
						<ProfileCard profile={profile} />
						<Button
							variant="ghost"
							size="sm"
							leftIcon={<HeartOff size={14} />}
							onClick={(e) => {
								e.preventDefault();
								setRemoveTarget(profile);
							}}
							className="w-full text-text-secondary hover:text-error"
						>
							Entfernen
						</Button>
					</div>
				)}
			/>

			{lastPage > 1 && (
				<Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={handlePageChange} />
			)}

			<ConfirmDialog
				open={removeTarget != null}
				onConfirm={handleConfirmRemove}
				onCancel={() => setRemoveTarget(null)}
				title="Favorit entfernen"
				message={`Möchtest du ${removeTarget?.name ?? ""} wirklich aus deinen Favoriten entfernen?`}
				confirmLabel="Entfernen"
				danger
				loading={toggleFavorite.isPending}
			/>

			<HotFlirtsSlider />
		</div>
	);
}
